import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { type Href, Link, usePathname, useSegments } from 'expo-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Portal } from '@gorhom/portal';

import {
  BodyText,
  Button,
  Navbar,
  theme,
  useNavbarContainerHeight,
  useResponsive,
  useToggleState,
  VStack,
} from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useTelemetry } from '@order/Telemetry';
import { useUsableNetBannerOffset } from '@order/usable-net';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useBagCount, useGroupOrderDetails, usePendingOrder } from '../../../../hooks';
import { useActiveScreenOptions } from '../../../../routing/hooks/useActiveScreenOptions';
import { LocationTooltip, useLocationTooltip } from '../../../LocationTooltip';
import { LINKS, NAVIGATION_CONTENT_TYPE, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarMoreContentLink } from './components/NavbarMoreContentLink';
import { useNavbarLocationData } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarSM = () => {
  const pathname = usePathname();
  const segments = useSegments();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { minWidth } = useResponsive();
  const { heightStyle: navbarHeightStyle } = useNavbarContainerHeight();
  const usableNetBannerOffset = useUsableNetBannerOffset();

  const { bagCount } = useBagCount();
  const pendingOrder = usePendingOrder({ requestPolicy: 'cache-only' });
  const { hostGroupOrderDetails, inviteeGroupOrderDetails } = useGroupOrderDetails();
  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();

  const {
    locationEyebrow,
    locationTitle,
    locationTooltipChannel,
    locationTooltipId,
    locationTooltipTarget,
    menuHref,
    orderChannel,
  } = useNavbarLocationData({ lastInteractedStore });

  // ─── State ───────────────────────────────────────────────────────────

  const { value: isMoreOpen, toggle: toggleMore, toggleOff: closeMore } = useToggleState();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data: navigationData } = useContentfulContentTypeEntry<NavigationContent>({
    contentType: NAVIGATION_CONTENT_TYPE,
    include: 2,
  });

  const moreMenuNavLinks = navigationData?.fields?.moreMenuNavLinks ?? [];

  // ─── Navigation ──────────────────────────────────────────────────────

  const screenOptions = useActiveScreenOptions();
  const navbarLayout = screenOptions?.navbarLayout ?? 'floating';

  const shouldRenderStaticContainer = navbarLayout !== 'floating';
  const shouldRenderStaticContainerBorder = navbarLayout === 'static-with-border';
  const shouldShowGlassShadow = navbarLayout === 'floating';
  const shouldRenderCompactNav = !minWidth.isMD;

  const locationSelectionHref = useMemo<Href>(
    () => ({ pathname: LINKS.LOCATION_SELECTION, params: { backToPath: pathname } }),
    [pathname],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isMenuActive = segments.some((segment) => segment === TABS.MENU);
  const isRewardsActive = segments.some((segment) => segment === TABS.REWARDS);
  const isHomeActive = segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE); // prettier-ignore

  const shouldShowCurrentOrder = isHomeActive && Boolean(pendingOrder);

  const shouldShowGroupOrderHost = !shouldShowCurrentOrder && Boolean(hostGroupOrderDetails);
  const shouldShowGroupOrderInvitee = !shouldShowCurrentOrder && Boolean(inviteeGroupOrderDetails);
  const shouldShowLocation =
    !shouldShowCurrentOrder && !shouldShowGroupOrderHost && !shouldShowGroupOrderInvitee;

  const locationTooltip = useLocationTooltip({
    canShow: isHomeActive && shouldShowLocation,
    channel: locationTooltipChannel,
    locationId: locationTooltipId,
    targetPosition: locationTooltipTarget,
  });

  // ─── Styles ─────────────────────────────────────────────────────────

  const staticNavbarContainerStyles = [
    styles.staticNavbarContainer,
    shouldRenderStaticContainerBorder ? styles.staticNavbarContainerBorder : undefined,
    navbarHeightStyle,
  ];

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleMorePress = useCallback(() => {
    track('nav_bar_more_selected');
    toggleMore();
  }, [toggleMore, track]);

  const handleHomePress = useCallback(() => {
    closeMore();
    track('nav_bar_home_selected');
  }, [closeMore, track]);

  const handleMenuPress = useCallback(() => {
    closeMore();
    track('nav_bar_menu_selected');
  }, [closeMore, track]);

  const handleRewardsPress = useCallback(() => {
    closeMore();
    track('nav_bar_rewards_selected');
  }, [closeMore, track]);

  const handleBagPress = useCallback(() => {
    closeMore();
    track('nav_bar_bag_selected');
  }, [closeMore, track]);

  const handleCurrentOrderPress = useCallback(() => {
    closeMore();
    track('nav_bar_order_status_selected');
    pendingOrder?.onPress();
  }, [closeMore, pendingOrder, track]);

  const handleLocationPress = useCallback(() => {
    closeMore();
    track('header_location_selected');
  }, [closeMore, track]);

  const handleLoginSignupPress = useCallback(() => {
    closeMore();
    track('nav_bar_login_signup_selected');
  }, [closeMore, track]);

  const handleMoreMenuPress = useCallback(() => {
    closeMore();
    track('nav_more_menu_selected');
  }, [closeMore, track]);

  const handleMoreRewardsPress = useCallback(() => {
    closeMore();
    track('nav_more_rewards_offers_selected');
  }, [closeMore, track]);

  const handleMoreOrderHistoryPress = useCallback(() => {
    closeMore();
    track('nav_more_order_history_selected');
  }, [closeMore, track]);

  const handleMoreAccountPress = useCallback(() => {
    closeMore();
    track('nav_more_account_selected');
  }, [closeMore, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Navbar.Floating.Container>
        {shouldRenderCompactNav ? (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Navbar.Floating.MenuButton
              accessibilityLabel={formatMessage(messages.openNavigation)}
              onPress={handleMorePress}
            />

            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>
          </Navbar.Floating.PrimaryNav>
        ) : (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>

            <Navbar.Floating.NavLinksList>
              <Link asChild href={menuHref}>
                <Navbar.Floating.Link
                  isActive={isMenuActive && !isMoreOpen}
                  onPress={handleMenuPress}
                >
                  <FormattedMessage {...messages.menu} />
                </Navbar.Floating.Link>
              </Link>

              <Link asChild href={LINKS.REWARDS}>
                <Navbar.Floating.Link
                  isActive={isRewardsActive && !isMoreOpen}
                  onPress={handleRewardsPress}
                >
                  <FormattedMessage {...messages.rewards} />
                </Navbar.Floating.Link>
              </Link>

              <Navbar.Floating.Link
                accessibilityRole="button"
                accessibilityState={{ expanded: isMoreOpen }}
                isActive={isMoreOpen}
                onPress={handleMorePress}
              >
                <FormattedMessage {...messages.more} />
              </Navbar.Floating.Link>
            </Navbar.Floating.NavLinksList>
          </Navbar.Floating.PrimaryNav>
        )}

        {isMoreOpen && shouldRenderCompactNav ? (
          <Portal>
            <Navbar.Floating.Drawer.Container
              accessibilityLabel={formatMessage(messages.navigationMenu)}
              backdropAccessibilityLabel={formatMessage(messages.closeNavigationBackdrop)}
              closeAccessibilityLabel={formatMessage(messages.closeNavigation)}
              onClose={closeMore}
            >
              <Navbar.Floating.Drawer.VStack>
                <Navbar.Floating.Drawer.NavLinksList>
                  <Link asChild href={menuHref}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreMenuPress}>
                      <FormattedMessage {...messages.menu} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>

                  <Link asChild href={LINKS.REWARDS}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreRewardsPress}>
                      <FormattedMessage {...messages.rewards} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>
                </Navbar.Floating.Drawer.NavLinksList>

                {moreMenuNavLinks.length > 0 ? (
                  <Navbar.Floating.Drawer.NavLinksList>
                    {moreMenuNavLinks.map((link) => (
                      <NavbarMoreContentLink
                        key={link.sys.id}
                        link={link}
                        variant="drawer"
                        onPress={closeMore}
                      />
                    ))}
                  </Navbar.Floating.Drawer.NavLinksList>
                ) : null}
              </Navbar.Floating.Drawer.VStack>

              <Navbar.Floating.Drawer.Footer>
                {isLoggedIn ? (
                  <Navbar.Floating.Drawer.NavLinksList hasDivider>
                    <Link asChild href={LINKS.ORDER_HISTORY}>
                      <Navbar.Floating.Drawer.NavLink onPress={handleMoreOrderHistoryPress}>
                        <FormattedMessage {...messages.viewOrderHistory} />
                      </Navbar.Floating.Drawer.NavLink>
                    </Link>

                    <Link asChild href={LINKS.ACCOUNT}>
                      <Navbar.Floating.Drawer.NavLink onPress={handleMoreAccountPress}>
                        <FormattedMessage {...messages.myAccount} />
                      </Navbar.Floating.Drawer.NavLink>
                    </Link>
                  </Navbar.Floating.Drawer.NavLinksList>
                ) : (
                  <Link asChild href={LINKS.AUTH} onPress={handleLoginSignupPress}>
                    <Button accessibilityRole="link" size="large">
                      <BodyText sizeMatch={['16']} bold style={styles.drawerAuthLabel}>
                        <FormattedMessage {...messages.signInCreateAccount} />
                      </BodyText>
                    </Button>
                  </Link>
                )}
              </Navbar.Floating.Drawer.Footer>
            </Navbar.Floating.Drawer.Container>
          </Portal>
        ) : null}

        {isMoreOpen && !shouldRenderCompactNav ? (
          <Portal>
            <Navbar.Floating.Container>
              <Navbar.Floating.Popup
                shadow
                onClose={closeMore}
                accessibilityLabel={formatMessage(messages.moreMenu)}
                topOffset={usableNetBannerOffset}
              >
                <VStack gap={0}>
                  {moreMenuNavLinks.map((link) => (
                    <NavbarMoreContentLink
                      key={link.sys.id}
                      link={link}
                      variant="page"
                      onPress={closeMore}
                    />
                  ))}
                </VStack>

                {isLoggedIn ? (
                  <Navbar.Floating.PopupFooter>
                    <Link asChild href={LINKS.ORDER_HISTORY}>
                      <Navbar.Floating.TextLink onPress={handleMoreOrderHistoryPress}>
                        <FormattedMessage {...messages.viewOrderHistory} />
                      </Navbar.Floating.TextLink>
                    </Link>

                    <Link asChild href={LINKS.ACCOUNT}>
                      <Navbar.Floating.TextLink onPress={handleMoreAccountPress}>
                        <FormattedMessage {...messages.myAccount} />
                      </Navbar.Floating.TextLink>
                    </Link>
                  </Navbar.Floating.PopupFooter>
                ) : null}
              </Navbar.Floating.Popup>
            </Navbar.Floating.Container>
          </Portal>
        ) : null}

        <Navbar.Floating.SecondaryNav>
          {isLoggedIn ? null : (
            <Link aria-label={formatMessage(messages.logInOrSignUp)} asChild href={LINKS.AUTH}>
              <Navbar.Floating.AuthButton
                shadow={shouldShowGlassShadow}
                onPress={handleLoginSignupPress}
              >
                <FormattedMessage {...messages.logInOrSignUp} />
              </Navbar.Floating.AuthButton>
            </Link>
          )}

          {shouldShowCurrentOrder && pendingOrder ? (
            <Navbar.Floating.CurrentOrder.Container
              shadow={shouldShowGlassShadow}
              title={`${pendingOrder.etaLabel} • ${pendingOrder.statusLabel}`}
            >
              <Link asChild href={pendingOrder.href}>
                <Navbar.Floating.CurrentOrder.LinkPressable onPress={handleCurrentOrderPress}>
                  {pendingOrder.actionLabel}
                </Navbar.Floating.CurrentOrder.LinkPressable>
              </Link>
            </Navbar.Floating.CurrentOrder.Container>
          ) : null}

          {shouldShowGroupOrderHost && hostGroupOrderDetails ? (
            <Navbar.Floating.GroupOrderHostIndicator
              eyebrow={formatMessage(messages.groupOrderLocationEyebrow, {
                orderChannel: hostGroupOrderDetails.orderChannel,
              })}
              shadow={shouldShowGlassShadow}
              title={hostGroupOrderDetails.locationName}
            />
          ) : null}

          {shouldShowGroupOrderInvitee && inviteeGroupOrderDetails ? (
            <Link asChild href={LINKS.BAG}>
              <Navbar.Floating.GroupOrderInviteeButton
                shadow={shouldShowGlassShadow}
                title={formatMessage(messages.groupOrderInviteeTitle, {
                  host_name: inviteeGroupOrderDetails.hostFirstName,
                })}
                onPress={handleBagPress}
              />
            </Link>
          ) : null}

          {shouldShowLocation ? (
            <View>
              <Link asChild href={locationSelectionHref} aria-label={locationTitle}>
                <Navbar.Floating.LocationButton
                  eyebrow={locationEyebrow}
                  shadow={shouldShowGlassShadow}
                  title={locationTitle}
                  onPress={handleLocationPress}
                />
              </Link>

              <LocationTooltip
                isVisible={locationTooltip.isVisible}
                orderChannel={orderChannel}
                horizontalAlignment="end"
                horizontalOffset={theme.spacing['3']}
                onDismiss={locationTooltip.dismiss}
              />
            </View>
          ) : null}

          <Link
            aria-label={formatMessage(messages.bagAccessibilityLabel, { count: bagCount })}
            asChild
            href={LINKS.BAG}
          >
            <Navbar.Floating.BagButton
              count={bagCount}
              shadow={shouldShowGlassShadow}
              onPress={handleBagPress}
            />
          </Link>
        </Navbar.Floating.SecondaryNav>
      </Navbar.Floating.Container>

      {shouldRenderStaticContainer ? <View style={staticNavbarContainerStyles} /> : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  staticNavbarContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  staticNavbarContainerBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  drawerAuthLabel: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationContent = {
  moreMenuNavLinks: ComponentProps<typeof NavbarMoreContentLink>['link'][];
};
